import CreateAxiosInstance from './axiosConfig';
const APIClient = CreateAxiosInstance(import.meta.env.VITE_API_BASE_URL);

export async function getUserPacketCollections() {
  return await APIClient.get('/user_packet_collection/user');
}

export async function updateUserPacketCollection(collectionId, { title, description }) {
  const data = {};
  if (title !== undefined) data.title = title;
  if (description !== undefined) data.description = description;
  return await APIClient.patch('/user_packet_collection/user', {
    collection_id: collectionId,
    action: 'update_collection',
    data,
  });
}

export async function addPacketToCollection(packetId, collectionId) {
  return await APIClient.patch('/user_packet_collection/user', {
    packet_id: packetId,
    collection_id: collectionId,
    action: 'add_packet_to',
  });
}

export async function removePacketFromCollection(packetId, collectionId) {
  return await APIClient.patch('/user_packet_collection/user', {
    packet_id: packetId,
    collection_id: collectionId,
    action: 'remove_packet_from',
  });
}

export async function addProjectToCollection(projectId, collectionId) {
  return await APIClient.patch('/user_packet_collection/user', {
    project_id: projectId,
    collection_id: collectionId,
    action: 'add_project_to',
  });
}

export async function removeProjectFromCollection(projectId, collectionId) {
  return await APIClient.patch('/user_packet_collection/user', {
    project_id: projectId,
    collection_id: collectionId,
    action: 'remove_project_from',
  });
}

// Bulk add — the multi-select analogue of the single add calls above. Atomic server-side: the
// caller must own every id, and the folder's item caps are checked before anything is linked.
export async function addItemsToCollection(collectionId, { packetIds = [], projectIds = [] }) {
  return await APIClient.patch('/user_packet_collection/user', {
    collection_id: collectionId,
    packet_ids: packetIds,
    project_ids: projectIds,
    action: 'add_items_to',
  });
}

// Bulk unlink — the multi-select analogue of the single remove calls above. Atomic server-side:
// if the caller can't remove every id, nothing is removed.
export async function removeItemsFromCollection(collectionId, { packetIds = [], projectIds = [] }) {
  return await APIClient.patch('/user_packet_collection/user', {
    collection_id: collectionId,
    packet_ids: packetIds,
    project_ids: projectIds,
    action: 'remove_items_from',
  });
}

export async function createUserPacketCollection(collectionName, packet_id, description) {
  return await APIClient.post('/user_packet_collection/user', {
    collection_name: collectionName,
    description: description,
    packet_id: packet_id,
  });
}

export async function deleteUserPacketCollection(collectionId) {
  return await APIClient.delete('/user_packet_collection/user', {
    data: {
      collection_id: collectionId,
    },
  });
}

// Public — viewable without an account, but only once the folder is shared (or by the owner /
// an active member; otherwise 404). Returns the collection, owner_name, packets, projects,
// user_owns_collection, user_is_member, and member_count.
export async function getPublicSharedFolder(collectionId) {
  return await APIClient.get('/user_packet_collection/shared', {
    params: { collection_id: collectionId },
  });
}

// ── Folder members ──
// Any folder can have members invited (owner only). Invited emails become pending members,
// reconciled to their account the first time they open the folder while signed in.
export async function inviteFolderMembers(collectionId, emails) {
  return await APIClient.post('/user_packet_collection/members', {
    collection_id: collectionId,
    emails,
  });
}

// Remove your own membership from a folder you're a member of (not the owner).
export async function leaveFolder(collectionId) {
  return await APIClient.delete('/user_packet_collection/members', {
    data: { collection_id: collectionId },
  });
}

// Mark a folder as shared (owner-only) so its public /shared page resolves — called when the
// owner opens the Share dialog, so a copied link works even before anyone is invited.
export async function publishFolder(collectionId) {
  return await APIClient.post('/user_packet_collection/publish', {
    collection_id: collectionId,
  });
}

// Copy a curated (admin-built) folder into your own account. You become the owner of a new
// private folder, and every project in it is deep-cloned into your account. Only copyable
// (library-host-owned) folders are accepted; returns the new folder in results.
export async function copyFolder(collectionId) {
  return await APIClient.post('/user_packet_collection/copy', {
    collection_id: collectionId,
  });
}
